import { signOut, getAuth, sendPasswordResetEmail, deleteUser, signInWithEmailAndPassword, getIdTokenResult, createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { auth } from "../../firebase";
import Swal from "sweetalert2";

// --- CSS --- //
import "../../styles/Swal.css";

export const navigateBasedOnRole = (user, navigate, email, setError) => {
  try {
    // Check if email is verified
    if (!user.emailVerified) {
      navigate("/verify-email", { state: { email } });
      return;
    }

    // Fetch custom claims for the logged-in user
    user.getIdTokenResult().then((idTokenResult) => {
      const userRole = idTokenResult.claims.role || "public"; // Default to 'public' if no role is set

      // Redirect based on the user's role
      if (userRole === "gymAccess") {
        navigate("/certify"); // Redirect to Gym Dashboard
      } else if (userRole === "user") {
        navigate("/my-profile"); // Redirect to User Profile
      } else {
        navigate("/login"); // Redirect to login
      }
    });
  } catch (err) {
    setError("Failed to determine user role or verify email.");
    console.error(err);
  }
};

export const handleEmailVerification = async (email, password, navigate, setError) => {
  setError(""); // Clear previous errors

  try {
    // Sign in the user with Firebase Auth
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Use the helper function to handle navigation
    navigateBasedOnRole(user, navigate, email, setError);
  } catch (err) {
    setError("Failed to log in. Check your credentials.");
    console.error(err);
  }
};

export const handleLogin = async (email, password, navigate, setError) => {
  setError(""); // Clear previous errors

  try {
    // Sign in the user with Firebase Auth
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Use the helper function to handle navigation
    navigateBasedOnRole(user, navigate, email, setError);
  } catch (err) {
    setError("Failed to log in. Check your credentials.");
    console.error(err);
  }
};

// Logout handler
export const handleLogout = async (navigate) => {
  try {
    await signOut(auth); // Sign out the user
    navigate("/login"); // Redirect to login page
  } catch (error) {
    console.error("Error logging out:", error.message);
  }
};

// Delete Account handler
export const deleteAccount = async () => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    Swal.fire({
      icon: 'error',
      title: 'No User Found',
      text: 'No user is currently logged in.'
    });
    return;
  }

  // Show confirmation Swal
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: "This will permanently delete your account. You won't be able to recover it.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: 'var(--button-delete-bg)',
    cancelButtonColor: 'var(--vert-cert-grey)',
    confirmButtonText: 'Delete',
    background: 'var(--background-secondary)',
    color: 'var(--text-primary)'
  });

  if (result.isConfirmed) {
    try {
      await deleteUser(user);
      Swal.fire({
        icon: 'success',
        title: 'Account Deleted',
        text: 'Your account has been deleted successfully.',
        confirmButtonColor: 'var(--vert-cert-gold)',
        confirmButtonText: 'OK',
        background: 'var(--background-secondary)',
        color: 'var(--text-primary)'
      });
    } catch (error) {
      // Handle errors if deletion fails
      Swal.fire({
        icon: 'error',
        title: 'Error Deleting Account',
        text: `Error: ${error.message}`
      });
    }
  } else {
    //Do Nothing, action cancelled
  }
};

// Function to get the current user
export const getCurrentUser = () => {
  const auth = getAuth();
  const user = auth.currentUser; // Get the currently signed-in user

  if (user) {
    return user;  // Return the UID of the current user
  } else {
    return null; // Return null if no user is logged in
  }
};

// Function to get the current user's UID
export const getCurrentUserUid = () => {
  const auth = getAuth();
  const user = auth.currentUser; // Get the currently signed-in user

  if (user) {
    return user.uid;  // Return the UID of the current user
  } else {
    return null; // Return null if no user is logged in
  }
};

// Function to get the current user's email
export const getCurrentUserEmail = () => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    return user.email; // Return the user's email
  } else {
    console.warn("No user is currently signed in.");
    return null;
  }
};

export const resetPassword = async (email) => {
  const auth = getAuth();

  try {
    // Send the password reset email
    await sendPasswordResetEmail(auth, email);

    // Inform the user that the email has been sent
    Swal.fire({
      icon: 'success',
      title: 'Password Reset Email Sent',
      text: `A password reset email has been sent to ${email}. Please check your inbox and follow the instructions.`,
      background: "var(--background-secondary)",
      color: "var(--text-primary)",
      confirmButtonColor: "var(--vert-cert-gold)",
      customClass: {
        popup: 'custom-popup',
        title: 'custom-title',
        content: 'custom-content',
      }
    });
  } catch (error) {
    // Handle different types of errors
    let errorMessage = "An error occurred while sending the password reset email.";
    if (error.code === "auth/invalid-email") {
      errorMessage = "The email address is not valid.";
    } else if (error.code === "auth/user-not-found") {
      errorMessage = "No user found with the provided email address.";
    } else if (error.code === "auth/too-many-requests") {
      errorMessage = "Too many requests. Please try again later.";
    }

    Swal.fire({
      icon: 'error',
      title: 'Error Resetting Password',
      text: errorMessage,
      background: "var(--background-secondary)",
      color: "var(--text-primary)",
      confirmButtonColor: "var(--vert-cert-gold)",
      customClass: {
        popup: 'custom-popup',
        title: 'custom-title',
        content: 'custom-content',
      }
    });
  }
};

export const handleSignUp = async ({ firstName, lastName, email, password, dateOfBirth }) => {
  const auth = await getAuth();

  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  try {
    await setDoc(doc(db, "UserProfiles", user.uid), {
      firstName,
      lastName,
      DOB: dateOfBirth,
    });
  } catch (firestoreError) {
    console.error("Error saving user data to Firestore:", firestoreError.message);
    throw new Error("Failed to save user data.");
  }

  try {
    console.error(user);
    await sendEmailVerification(user);
  } catch (verificationError) {
    console.error("Error sending email verification:", verificationError.message);
    throw new Error("Failed to send email verification.");
  }

  try {
    const idToken = await user.getIdToken();
    await fetch("https://setuserrole-jhx3rmciwq-uc.a.run.app", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ uid: user.uid, role: "user" }),
    });
  } catch (roleError) {
    console.error("Error setting user role:", roleError.message);
    throw new Error("Failed to set user role.");
  }

  return user;
};